<template>
  <div class="feedback-dashboard w-full">
    <section class="dashboard-hero">
      <div>
        <div class="hero-kicker">Thống kê góp ý</div>
        <h1>Dashboard góp ý người dân</h1>
        <p>Theo dõi số lượng góp ý theo ngày, tháng, quý và năm.</p>
      </div>
      <Button
        label="Làm mới"
        icon="pi pi-refresh"
        :loading="isLoading"
        @click="loadDashboard"
      />
    </section>

    <section class="filter-card">
      <div class="filter-grid">
        <div class="field-block">
          <label>Từ thời gian</label>
          <Calendar v-model="timeFrom" showTime placeholder="Chọn thời gian bắt đầu" />
        </div>
        <div class="field-block">
          <label>Đến thời gian</label>
          <Calendar v-model="timeTo" showTime placeholder="Chọn thời gian kết thúc" />
        </div>
        <div class="field-block">
          <label>Phân loại</label>
          <Dropdown
            v-model="selectedFeedbackKindId"
            :options="feedbackKindOptions"
            optionLabel="content"
            optionValue="id"
            placeholder="Tất cả"
            :filter="true"
            @change="loadDashboard"
          />
        </div>
        <div class="quick-actions">
          <Button label="Hôm nay" outlined @click="selectToday" />
          <Button label="7 ngày" outlined @click="selectLast7Days" />
          <Button label="Tháng này" outlined @click="selectThisMonth" />
          <Button label="Năm này" outlined @click="selectThisYear" />
        </div>
      </div>
    </section>

    <section class="metric-grid">
      <div class="metric-card metric-total">
        <span>Tổng góp ý</span>
        <strong>{{ totalFeedback }}</strong>
        <small>Trong khoảng đang chọn</small>
      </div>
      <div class="metric-card">
        <span>Ngày nhiều nhất</span>
        <strong>{{ peakDay.value }}</strong>
        <small>{{ peakDay.label }}</small>
      </div>
      <div class="metric-card">
        <span>Tháng nhiều nhất</span>
        <strong>{{ peakMonth.value }}</strong>
        <small>{{ peakMonth.label }}</small>
      </div>
      <div class="metric-card">
        <span>Phân loại</span>
        <strong>{{ kindCount }}</strong>
        <small>Nhóm góp ý phát sinh</small>
      </div>
    </section>

    <section class="chart-grid">
      <article class="chart-card chart-wide">
        <div class="chart-title-row">
          <div>
            <h2>Góp ý theo ngày</h2>
            <p>Diễn biến chi tiết trong khoảng thời gian lọc.</p>
          </div>
          <span>{{ dailyLabels.length }} ngày</span>
        </div>
        <div class="chart-shell">
          <canvas ref="dailyCanvas"></canvas>
        </div>
      </article>

      <article class="chart-card">
        <div class="chart-title-row">
          <div>
            <h2>Góp ý theo tháng</h2>
            <p>Tổng hợp theo từng tháng.</p>
          </div>
          <span>{{ monthlyLabels.length }} tháng</span>
        </div>
        <div class="chart-shell">
          <canvas ref="monthlyCanvas"></canvas>
        </div>
      </article>

      <article class="chart-card">
        <div class="chart-title-row">
          <div>
            <h2>Góp ý theo quý</h2>
            <p>So sánh khối lượng góp ý theo quý.</p>
          </div>
          <span>{{ quarterlyLabels.length }} quý</span>
        </div>
        <div class="chart-shell">
          <canvas ref="quarterlyCanvas"></canvas>
        </div>
      </article>

      <article class="chart-card chart-wide">
        <div class="chart-title-row">
          <div>
            <h2>Góp ý theo năm</h2>
            <p>Xu hướng dài hạn qua các năm.</p>
          </div>
          <span>{{ yearlyLabels.length }} năm</span>
        </div>
        <div class="chart-shell">
          <canvas ref="yearlyCanvas"></canvas>
        </div>
      </article>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from "vue";
import { Chart, registerables } from "chart.js";
import { useToast } from "@/ui/services/toast";
import zaloRepository from "@/services/ZaloRepository";
import { FeedbackItem, FeedbackKind } from "@/models/feedback";
import {
  getVietnamStartOfDayDaysAgoValue,
  getVietnamStartOfMonthValue,
  getVietnamStartOfTodayValue,
  toVietnamDateTimeLocalValue,
  vietnamDateTimeLocalToIso,
} from "@/utils/vietnamDateTime";

Chart.register(...registerables);

type BucketMap = Record<string, number>;
type FeedbackApiItem = FeedbackItem & {
  Time?: string;
  IdKind?: number;
  Kind?: string;
};

export default {
  name: "FeedbackDashboardPage",
  setup() {
    const toast = useToast();
    const timeFrom = ref("");
    const timeTo = ref(toVietnamDateTimeLocalValue(new Date()));
    const selectedFeedbackKindId = ref(0);
    const feedbackKindOptions = ref<FeedbackKind[]>([
      { id: 0, content: "Tất cả" },
    ]);
    const feedbackItems = ref<FeedbackItem[]>([]);
    const isLoading = ref(false);

    const dailyCanvas = ref<HTMLCanvasElement | null>(null);
    const monthlyCanvas = ref<HTMLCanvasElement | null>(null);
    const quarterlyCanvas = ref<HTMLCanvasElement | null>(null);
    const yearlyCanvas = ref<HTMLCanvasElement | null>(null);
    const charts: Chart[] = [];

    const getVietnamYear = () =>
      new Date().toLocaleString("en-US", {
        timeZone: "Asia/Ho_Chi_Minh",
        year: "numeric",
      });

    timeFrom.value = `${getVietnamYear()}-01-01T00:00`;

    const normalizeFeedbackItems = (data: any): FeedbackItem[] => {
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.items)
            ? data.items
            : [];
      const selectedKind = Number(selectedFeedbackKindId.value || 0);

      return rows
        .map((item: FeedbackApiItem) => ({
          ...item,
          time: item.time || item.Time || "",
          idKind: item.idKind ?? item.IdKind,
          kind: item.kind || item.Kind || "Góp ý chung",
        }))
        .filter((item: FeedbackItem) => {
          if (!selectedKind) return true;
          return Number(item.idKind || 1) === selectedKind;
        });
    };

    const parseDate = (value: string) => {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const dayKey = (date: Date) =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Ho_Chi_Minh",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(date);

    const monthKey = (date: Date) =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Ho_Chi_Minh",
        year: "numeric",
        month: "2-digit",
      }).format(date);

    const yearKey = (date: Date) =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Ho_Chi_Minh",
        year: "numeric",
      }).format(date);

    const quarterKey = (date: Date) => {
      const [year, month] = monthKey(date).split("-").map(Number);
      return `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
    };

    const toBucket = (getKey: (date: Date) => string): BucketMap => {
      return feedbackItems.value.reduce((result, item) => {
        const date = parseDate(item.time);
        if (!date) return result;
        const key = getKey(date);
        result[key] = (result[key] || 0) + 1;
        return result;
      }, {} as BucketMap);
    };

    const sortKeys = (bucket: BucketMap) => Object.keys(bucket).sort();
    const formatDay = (key: string) => {
      const [year, month, day] = key.split("-");
      return `${day}/${month}/${year}`;
    };
    const formatMonth = (key: string) => {
      const [year, month] = key.split("-");
      return `Tháng ${Number(month)}/${year}`;
    };
    const formatMonthShort = (key: string) => {
      const [, month] = key.split("-");
      return `T${Number(month)}`;
    };
    const formatQuarter = (key: string) => {
      const [year, quarter] = key.split("-Q");
      return `Quý ${quarter}/${year}`;
    };
    const formatQuarterShort = (key: string) => {
      const [, quarter] = key.split("-Q");
      return `Q${quarter}`;
    };

    const dailyBucket = computed(() => toBucket(dayKey));
    const monthlyBucket = computed(() => toBucket(monthKey));
    const quarterlyBucket = computed(() => toBucket(quarterKey));
    const yearlyBucket = computed(() => toBucket(yearKey));
    const selectedYear = computed(() => {
      const year = Number((timeFrom.value || "").slice(0, 4));
      return Number.isFinite(year) && year > 0 ? year : Number(getVietnamYear());
    });
    const fullMonthLabels = computed(() =>
      Array.from({ length: 12 }, (_, index) => {
        const month = String(index + 1).padStart(2, "0");
        return `${selectedYear.value}-${month}`;
      }),
    );
    const fullQuarterLabels = computed(() =>
      Array.from({ length: 4 }, (_, index) => `${selectedYear.value}-Q${index + 1}`),
    );

    const dailyLabels = computed(() => sortKeys(dailyBucket.value));
    const monthlyLabels = computed(() => fullMonthLabels.value);
    const quarterlyLabels = computed(() => fullQuarterLabels.value);
    const yearlyLabels = computed(() => sortKeys(yearlyBucket.value));

    const totalFeedback = computed(() => feedbackItems.value.length);
    const kindCount = computed(
      () => new Set(feedbackItems.value.map((item) => item.kind || "Khác")).size,
    );

    const getPeak = (
      labels: string[],
      bucket: BucketMap,
      formatter: (value: string) => string,
    ) => {
      if (!labels.length) return { label: "Chưa có dữ liệu", value: 0 };
      const key = labels.reduce((best, item) =>
        bucket[item] > bucket[best] ? item : best,
      );
      return { label: formatter(key), value: bucket[key] || 0 };
    };

    const peakDay = computed(() =>
      getPeak(dailyLabels.value, dailyBucket.value, formatDay),
    );
    const peakMonth = computed(() =>
      getPeak(monthlyLabels.value, monthlyBucket.value, formatMonth),
    );

    const destroyCharts = () => {
      while (charts.length) {
        charts.pop()?.destroy();
      }
    };

    const chartColors = ["#14b8a6", "#2563eb", "#f97316", "#e11d48"];

    const createChart = (
      canvas: HTMLCanvasElement | null,
      type: "bar" | "line",
      labels: string[],
      values: number[],
      label: string,
      color: string,
    ) => {
      if (!canvas) return;
      const context = canvas.getContext("2d");
      if (!context) return;

      const gradient = context.createLinearGradient(0, 0, 0, 260);
      gradient.addColorStop(0, `${color}88`);
      gradient.addColorStop(1, `${color}12`);
      const maxValue = Math.max(0, ...values);
      const isBar = type === "bar";

      charts.push(
        new Chart(canvas, {
          type,
          data: {
            labels,
            datasets: [
              {
                label,
                data: values,
                backgroundColor: isBar ? `${color}d9` : gradient,
                borderColor: color,
                borderWidth: isBar ? 0 : 3,
                borderRadius: isBar ? 8 : 10,
                borderSkipped: false,
                barPercentage: isBar ? 0.64 : undefined,
                categoryPercentage: isBar ? 0.72 : undefined,
                maxBarThickness: isBar ? 28 : undefined,
                fill: type === "line",
                tension: 0.38,
                pointRadius: type === "line" ? 4 : 0,
                pointHoverRadius: 6,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: { top: 8, right: 10, bottom: 0, left: 0 },
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: "#0f172a",
                padding: 12,
                titleFont: { size: 13, weight: "700" },
                bodyFont: { size: 13, weight: "600" },
                displayColors: false,
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: {
                  color: "#64748b",
                  font: { size: 12, weight: "600" },
                  autoSkip: labels.length > 16,
                  maxRotation: labels.length > 12 ? 35 : 0,
                  minRotation: labels.length > 12 ? 35 : 0,
                },
              },
              y: {
                beginAtZero: true,
                suggestedMax: maxValue <= 0 ? 1 : Math.ceil(maxValue * 1.2),
                ticks: {
                  precision: 0,
                  color: "#64748b",
                  font: { size: 12, weight: "600" },
                },
                grid: { color: "#e2e8f0", borderDash: [5, 5] },
              },
            },
          },
        }),
      );
    };

    const renderCharts = async () => {
      await nextTick();
      destroyCharts();

      createChart(
        dailyCanvas.value,
        "line",
        dailyLabels.value.map(formatDay),
        dailyLabels.value.map((key) => dailyBucket.value[key] || 0),
        "Góp ý theo ngày",
        chartColors[0],
      );
      createChart(
        monthlyCanvas.value,
        "bar",
        monthlyLabels.value.map(formatMonthShort),
        monthlyLabels.value.map((key) => monthlyBucket.value[key] || 0),
        "Góp ý theo tháng",
        chartColors[1],
      );
      createChart(
        quarterlyCanvas.value,
        "bar",
        quarterlyLabels.value.map(formatQuarterShort),
        quarterlyLabels.value.map((key) => quarterlyBucket.value[key] || 0),
        "Góp ý theo quý",
        chartColors[2],
      );
      createChart(
        yearlyCanvas.value,
        "line",
        yearlyLabels.value,
        yearlyLabels.value.map((key) => yearlyBucket.value[key] || 0),
        "Góp ý theo năm",
        chartColors[3],
      );
    };

    const loadFeedbackKind = () => {
      zaloRepository
        .getListFeedbackKind()
        .then((response) => {
          feedbackKindOptions.value = [
            { id: 0, content: "Tất cả" },
            ...(response.data || []),
          ];
        })
        .catch(() => {
          feedbackKindOptions.value = [{ id: 0, content: "Tất cả" }];
          toast.add({
            severity: "warn",
            summary: "Phân loại góp ý",
            detail: "Chưa tải được danh mục phân loại, hệ thống đang hiển thị tất cả.",
            life: 2500,
          });
        });
    };

    const loadDashboard = async () => {
      const timeFromIso = vietnamDateTimeLocalToIso(timeFrom.value);
      const timeToIso = vietnamDateTimeLocalToIso(timeTo.value);

      if (!timeFromIso || !timeToIso) {
        toast.add({
          severity: "error",
          summary: "Lỗi",
          detail: "Khoảng thời gian không hợp lệ",
          life: 2500,
        });
        return;
      }

      isLoading.value = true;
      try {
        const response = await zaloRepository.getListFeedBack({
          timeFrom: timeFromIso,
          timeTo: timeToIso,
          idKind: Number(selectedFeedbackKindId.value || 0),
        });
        feedbackItems.value = normalizeFeedbackItems(response.data);
        await renderCharts();
      } catch (err: any) {
        toast.add({
          severity: "error",
          summary: "Lỗi",
          detail: err.response?.data || "Lỗi khi tải thống kê góp ý",
          life: 2500,
        });
      } finally {
        isLoading.value = false;
      }
    };

    const selectToday = () => {
      timeFrom.value = getVietnamStartOfTodayValue();
      timeTo.value = toVietnamDateTimeLocalValue(new Date());
      nextTick(loadDashboard);
    };

    const selectLast7Days = () => {
      timeFrom.value = getVietnamStartOfDayDaysAgoValue(6);
      timeTo.value = toVietnamDateTimeLocalValue(new Date());
      nextTick(loadDashboard);
    };

    const selectThisMonth = () => {
      timeFrom.value = getVietnamStartOfMonthValue();
      timeTo.value = toVietnamDateTimeLocalValue(new Date());
      nextTick(loadDashboard);
    };

    const selectThisYear = () => {
      timeFrom.value = `${getVietnamYear()}-01-01T00:00`;
      timeTo.value = toVietnamDateTimeLocalValue(new Date());
      nextTick(loadDashboard);
    };

    loadFeedbackKind();
    loadDashboard();
    onBeforeUnmount(destroyCharts);

    return {
      timeFrom,
      timeTo,
      selectedFeedbackKindId,
      feedbackKindOptions,
      isLoading,
      totalFeedback,
      kindCount,
      peakDay,
      peakMonth,
      dailyLabels,
      monthlyLabels,
      quarterlyLabels,
      yearlyLabels,
      dailyCanvas,
      monthlyCanvas,
      quarterlyCanvas,
      yearlyCanvas,
      loadDashboard,
      selectToday,
      selectLast7Days,
      selectThisMonth,
      selectThisYear,
    };
  },
};
</script>

<style scoped>
.feedback-dashboard {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-bottom: 24px;
}

.dashboard-hero {
  align-items: center;
  background:
    linear-gradient(135deg, rgba(15, 118, 110, 0.96), rgba(37, 99, 235, 0.9)),
    radial-gradient(circle at 88% 20%, rgba(255, 255, 255, 0.22), transparent 30%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 18px;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.14);
  color: white;
  display: flex;
  justify-content: space-between;
  min-height: 132px;
  padding: 22px 26px;
}

.hero-kicker {
  color: rgba(255, 255, 255, 0.78);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.dashboard-hero h1 {
  font-size: 30px;
  font-weight: 950;
  line-height: 1.15;
  margin: 6px 0;
}

.dashboard-hero p {
  color: rgba(255, 255, 255, 0.86);
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.filter-card,
.chart-card,
.metric-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #dbe6ef;
  border-radius: 14px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
}

.filter-card {
  padding: 16px;
}

.filter-grid {
  align-items: end;
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

.field-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: span 3;
  min-width: 0;
}

.field-block label {
  color: #475569;
  font-size: 13px;
  font-weight: 900;
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: span 3;
}

.metric-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-card {
  min-height: 112px;
  overflow: hidden;
  padding: 18px 20px;
  position: relative;
}

.metric-card::after {
  background: linear-gradient(135deg, rgba(20, 184, 166, 0.2), rgba(37, 99, 235, 0.08));
  border-radius: 999px;
  content: "";
  height: 92px;
  position: absolute;
  right: -28px;
  top: -28px;
  width: 92px;
}

.metric-card span {
  color: #64748b;
  display: block;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.metric-card strong {
  color: #0f172a;
  display: block;
  font-size: 34px;
  font-weight: 950;
  line-height: 1.1;
  margin-top: 8px;
}

.metric-card small {
  color: #64748b;
  display: block;
  font-size: 13px;
  font-weight: 700;
  margin-top: 8px;
}

.metric-total {
  border-left: 5px solid #14b8a6;
}

.chart-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.chart-card {
  padding: 20px;
}

.chart-wide {
  grid-column: span 2;
}

.chart-title-row {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.chart-title-row h2 {
  color: #0f172a;
  font-size: 20px;
  font-weight: 950;
  line-height: 1.2;
  margin: 0;
}

.chart-title-row p {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  margin: 5px 0 0;
}

.chart-title-row span {
  background: #ecfdf5;
  border-radius: 999px;
  color: #0f766e;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 900;
  padding: 7px 10px;
}

.chart-shell {
  height: 320px;
  min-width: 0;
}

.chart-wide .chart-shell {
  height: 360px;
}

@media (max-width: 1024px) {
  .field-block,
  .quick-actions {
    grid-column: span 6;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .chart-grid,
  .chart-wide {
    grid-template-columns: 1fr;
  }

  .chart-wide {
    grid-column: span 1;
  }
}

@media (max-width: 640px) {
  .dashboard-hero {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }

  .dashboard-hero h1 {
    font-size: 24px;
  }

  .field-block,
  .quick-actions {
    grid-column: span 12;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .chart-shell,
  .chart-wide .chart-shell {
    height: 300px;
  }
}
</style>
